import {
  FetchError,
  getPrimarySubaccountAddr,
  UserSubaccount,
  UserSubaccounts,
} from "@decibeltrade/sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import { readDexSdk, sdkConfig } from "@/contexts/dex-sdk";
import { userSubaccountsQueryKey } from "@/contexts/queries/user-subaccounts";
import { addressesEqual } from "@/utils/address";

interface SubmitInvitationCodeParams {
  code: string;
  account: string;
}

interface SubmitInvitationCodeResponse {
  success: boolean;
  message: string;
}

export const submitInvitationCode = async (
  code: string,
  account: string,
  t: ReturnType<typeof useTranslations<"referralCode">>,
): Promise<SubmitInvitationCodeResponse> => {
  if (!account) {
    throw new Error("Account address is required");
  }

  // Callers (wallet selector modal, /r/<code> deep link → HandlePendingReferralCode)
  // are responsible for any pre-validation. The backend handles same-code retries
  // and orphan recovery (subaccount-exists, no activity, no prior redemption) as 200;
  // real conflicts (code exhausted, activity gate, prior different-code redemption)
  // come back as 409 with a user-readable message.
  try {
    await readDexSdk.referrals.redeemCode({
      referralCode: code,
      account,
    });

    return {
      success: true,
      message: t("codeRedeemed"),
    };
  } catch (error) {
    if (error instanceof FetchError && error.responseMessage) {
      throw new Error(error.responseMessage);
    }
    throw new Error(t("somethingWentWrong"));
  }
};

export function useSubmitInvitationCode() {
  const queryClient = useQueryClient();
  const t = useTranslations("referralCode");

  return useMutation<SubmitInvitationCodeResponse, Error, SubmitInvitationCodeParams>({
    mutationFn: ({ code, account }: SubmitInvitationCodeParams) => {
      return submitInvitationCode(code, account, t);
    },
    onSuccess: (_payload, { account }) => {
      if (!account) return;
      queryClient.setQueryData(
        userSubaccountsQueryKey({ ownerAddr: account }),
        (prev: UserSubaccounts | undefined): UserSubaccounts => {
          const dataToInsert: UserSubaccount = {
            subaccount_address: getPrimarySubaccountAddr(
              account,
              sdkConfig.compatVersion,
              sdkConfig.deployment.package,
            ),
            is_active: true,
            is_primary: true,
            primary_account_address: account,
            custom_label: null,
          };

          // Backend can 200 even when the primary subaccount already existed
          // (orphan recovery path). Dedupe by address so we don't end up with
          // two `is_primary: true` rows in the cache.
          const rest =
            prev?.filter(
              (s) => !addressesEqual(s.subaccount_address, dataToInsert.subaccount_address),
            ) ?? [];
          return [dataToInsert, ...rest];
        },
      );
    },
  });
}
